import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  increment,
  setDoc,
  runTransaction
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Points, TeamPoints, PointsTransaction, Achievement } from '../types';
import { Capacitor } from '@capacitor/core';
import { firestoreRestApi } from '../utils/firestoreRestApi';

// Run activity types that earn activity XP (no miles/minutes required)
const ACTIVITY_RUN_TYPES = ['Run', 'Workout', 'Strength', 'Cross Training', 'Rest Day', 'Sick', 'Injured'] as const;

export const pointsService = {
  // Points calculation constants
  POINTS: {
    // Timing-based daily points (XP and WP)
    // Base: 0.5 for health metrics + 0.5 for activity = 1.0 total if both completed
    DAILY_FULL_POINTS: 1.0,    // By 8 AM next day (full credit: 0.5 health + 0.5 activity)
    DAILY_HALF_POINTS: 0.5,    // After 8 AM but before 3 PM next day (half credit: 0.25 health + 0.25 activity)
    DAILY_AFTER_DEADLINE: 0,   // After 3 PM next day (no points)
    
    // Challenge and Mission rewards (WP only)
    CHALLENGE_MIN: 1,
    CHALLENGE_MAX: 5,
    MISSION_REWARD: 10,
    
    // Legacy rewards
    GOAL_COMPLETION: 25,
    DISTANCE_MILESTONE: 50,
    CONSISTENCY_BONUS: 20
  },

  // Calculate timing multiplier based on submission time
  // New rules:
  // - By 8 AM next day: Full points (1.0x = 0.5 health + 0.5 activity = 1.0 total)
  // - After 8 AM but before 3 PM next day: Half points (0.5x = 0.25 health + 0.25 activity = 0.5 total)
  // - After 3 PM next day: No points (0x)
  calculateTimingMultiplier(submissionDate: Date, activityDate: string): {
    multiplier: number;
    timing: 'same_day' | 'early_next' | 'late_next' | 'no_points';
  } {
    // Parse activity date in local timezone to avoid UTC conversion issues
    const activityDateObj = new Date(activityDate + 'T12:00:00'); // Use noon to avoid timezone issues
    const submissionTime = submissionDate.getHours() * 60 + submissionDate.getMinutes();
    
    // Check if submission is same day
    const isSameDay = submissionDate.toDateString() === activityDateObj.toDateString();
    
    if (isSameDay) {
      // Same day submission - gets full points (before 8 AM next day deadline)
      return {
        multiplier: this.POINTS.DAILY_FULL_POINTS, // 1.0
        timing: 'same_day'
      };
    } else {
      // Next day submission
      const nextDay = new Date(activityDateObj);
      nextDay.setDate(nextDay.getDate() + 1);
      
      // Check if submission is next day
      const isNextDay = submissionDate.toDateString() === nextDay.toDateString();
      
      if (isNextDay) {
        // 8 AM = 480 minutes, 3 PM = 900 minutes
        if (submissionTime <= 480) {
          // By 8 AM next day - full points
          return {
            multiplier: this.POINTS.DAILY_FULL_POINTS, // 1.0
            timing: 'early_next'
          };
        } else if (submissionTime <= 900) {
          // After 8 AM but before 3 PM next day - half points
          return {
            multiplier: this.POINTS.DAILY_HALF_POINTS, // 0.5
            timing: 'late_next'
          };
        } else {
          // After 3 PM next day - no points
          return {
            multiplier: this.POINTS.DAILY_AFTER_DEADLINE, // 0
            timing: 'no_points'
          };
        }
      } else {
        // More than one day late - no points
        return {
          multiplier: this.POINTS.DAILY_AFTER_DEADLINE, // 0
          timing: 'no_points'
        };
      }
    }
  },

  // Calculate timing-based points for daily logging
  // XP is split: 0.5 for health metrics + 0.5 for activity, then timing penalties apply
  calculateTimingBasedPoints(
    submissionDate: Date, 
    activityDate: string,
    hasHealthMetrics: boolean,
    hasActivity: boolean
  ): {
    experiencePoints: number;
    walletPoints: number;
    timing: 'same_day' | 'early_next' | 'late_next' | 'no_points';
    healthXP: number;
    activityXP: number;
  } {
    // Get timing multiplier
    const { multiplier, timing } = this.calculateTimingMultiplier(submissionDate, activityDate);
    
    // Base XP values (before penalties)
    const BASE_HEALTH_XP = 0.5;
    const BASE_ACTIVITY_XP = 0.5;
    
    // Calculate XP for each component (apply timing multiplier)
    const healthXP = hasHealthMetrics ? BASE_HEALTH_XP * multiplier : 0;
    const activityXP = hasActivity ? BASE_ACTIVITY_XP * multiplier : 0;
    
    // Total XP is sum of both components
    const experiencePoints = healthXP + activityXP;
    
    // Wallet points use the same calculation (timing affects both XP and WP)
    const walletPoints = experiencePoints;
    
    return {
      experiencePoints,
      walletPoints,
      timing,
      healthXP,
      activityXP
    };
  },

  // Calculate level from experience points
  // Level thresholds:
  // - Level 1: 0-9 XP
  // - Level 2: 10-24 XP
  // - Level 3: 25-39 XP
  // - Level 4: 40-54 XP
  // - Level 5: 55-74 XP
  // - Prestige: 75+ XP (returns 6 to represent prestige)
  calculateLevelFromXP(experiencePoints: number): number {
    if (experiencePoints < 10) {
      return 1; // 0-9 XP = Level 1
    } else if (experiencePoints < 25) {
      return 2; // 10-24 XP = Level 2
    } else if (experiencePoints < 40) {
      return 3; // 25-39 XP = Level 3
    } else if (experiencePoints < 55) {
      return 4; // 40-54 XP = Level 4
    } else if (experiencePoints < 75) {
      return 5; // 55-74 XP = Level 5
    } else {
      return 6; // 75+ XP = Prestige Level (represented as Level 6)
    }
  },
  
  // Check if user is at prestige level
  isPrestigeLevel(experiencePoints: number): boolean {
    return experiencePoints >= 75;
  },
  
  // Get level display string (handles prestige level)
  getLevelDisplayString(experiencePoints: number): string {
    const level = this.calculateLevelFromXP(experiencePoints);
    if (level === 6) {
      return 'Prestige Level';
    }
    return `Level ${level}`;
  },

  // Compute current streak from actual logged activity dates so out-of-order
  // submissions (e.g. logging yesterday after today) don't break streak state.
  calculateCurrentStreakFromDates(dateStrings: string[]): { currentStreak: number; lastLoggedDate: string } {
    if (!dateStrings.length) {
      return { currentStreak: 0, lastLoggedDate: '' };
    }

    const uniqueDates = Array.from(
      new Set(
        dateStrings
          .filter((date): date is string => typeof date === 'string' && date.length > 0)
      )
    );

    if (!uniqueDates.length) {
      return { currentStreak: 0, lastLoggedDate: '' };
    }

    uniqueDates.sort();
    const lastLoggedDate = uniqueDates[uniqueDates.length - 1];
    const dateSet = new Set(uniqueDates);

    let streak = 0;
    let cursor = new Date(`${lastLoggedDate}T12:00:00`);
    while (true) {
      const cursorDate = cursor.toISOString().split('T')[0];
      if (!dateSet.has(cursorDate)) {
        break;
      }
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    }

    return {
      currentStreak: streak,
      lastLoggedDate
    };
  },

  async recomputeStreakFromActivities(userId: string, teamId: string): Promise<{ currentStreak: number; lastLoggedDate: string } | null> {
    try {
      const { activityService } = await import('./activityService');
      // High cap to include full streak windows and reduce order-dependence.
      const activities = await activityService.getUserActivities(userId, 2000, teamId);
      const activityDates = activities
        .map(activity => activity.date)
        .filter((date): date is string => typeof date === 'string' && date.length > 0);

      return this.calculateCurrentStreakFromDates(activityDates);
    } catch (error) {
      console.warn('[PointsService] Failed to recompute streak from activity history:', error);
      return null;
    }
  },

  // iOS REST API version of awardDailyLoggingPoints
  async awardDailyLoggingPointsViaRest(
    userId: string,
    teamId: string,
    activityDate: string,
    submissionDate: Date,
    activityData?: {
      sleepScore?: number;
      hydrationLevel?: number;
      nutritionLevel?: number;
      runType?: 'Run' | 'Workout' | 'Strength' | 'Cross Training' | 'Rest Day' | 'Sick' | 'Injured' | 'None';
      milesRun?: number;
      crossTrainingMinutes?: number;
      strengthMinutes?: number;
    }
  ): Promise<{
    experiencePoints: number;
    walletPoints: number;
    streakBonus: number;
    newStreak: number;
    newLevel: number;
    timing: string;
    healthXP: number;
    activityXP: number;
  }> {
    try {
      console.log('[PointsService REST] Starting points award via REST API');
      console.log('[PointsService REST] Input:', { userId, teamId, activityDate, activityData });
      
      // Fetch existing activities for this date to determine:
      // 1. If health metrics exist (across all activities for the date)
      // 2. If activity exists (across all activities for the date)
      // 3. The earliest submission time (for accurate timing calculation)
      let existingActivities: any[] = [];
      let earliestSubmissionTime: Date = submissionDate;
      
      try {
        const { activityService } = await import('./activityService');
        existingActivities = await activityService.getUserActivitiesForDate(userId, activityDate, teamId);
        
        // Find earliest submission time from all activities for this date
        if (existingActivities.length > 0) {
          const submissionTimes = existingActivities
            .map(activity => {
              if (activity.submittedAt) {
                // Handle both Timestamp and Date formats
                if (activity.submittedAt.toDate) {
                  return activity.submittedAt.toDate();
                } else if (activity.submittedAt.seconds) {
                  return new Date(activity.submittedAt.seconds * 1000);
                } else if (activity.submittedAt instanceof Date) {
                  return activity.submittedAt;
                } else if (typeof activity.submittedAt === 'string') {
                  return new Date(activity.submittedAt);
                }
              }
              return null;
            })
            .filter((time): time is Date => time !== null);
          
          if (submissionTimes.length > 0) {
            earliestSubmissionTime = submissionTimes.reduce((earliest, current) => 
              current < earliest ? current : earliest
            );
          }
        }
      } catch (fetchError) {
        console.warn('[PointsService REST] Could not fetch existing activities for timing calculation:', fetchError);
        // Continue with current submission time if fetch fails
      }
      
      // Determine if health metrics exist (in current submission OR existing activities)
      const hasHealthMetricsInCurrent = activityData ? (
        (activityData.sleepScore !== undefined && activityData.sleepScore !== null && activityData.sleepScore > 0) ||
        (activityData.hydrationLevel !== undefined && activityData.hydrationLevel !== null && activityData.hydrationLevel > 0) ||
        (activityData.nutritionLevel !== undefined && activityData.nutritionLevel !== null && activityData.nutritionLevel > 0)
      ) : false;
      
      const hasHealthMetricsInExisting = existingActivities.some(activity =>
        (activity.sleepScore > 0) || (activity.hydrationLevel > 0) || (activity.nutritionLevel > 0)
      );
      
      const hasHealthMetrics = hasHealthMetricsInCurrent || hasHealthMetricsInExisting;
      
      // Determine if activity exists (in current submission OR existing activities)
      // All run activity types earn points: Run, Workout (legacy), Strength, Cross Training, Rest Day, Sick, Injured
      // No miles/minutes required - recording the activity type is enough
      const hasActivityInCurrent = activityData ? (
        activityData.runType !== undefined && 
        activityData.runType !== null && 
        activityData.runType !== 'None' &&
        ACTIVITY_RUN_TYPES.includes(activityData.runType)
      ) : false;
      
      const hasActivityInExisting = existingActivities.some(activity =>
        activity.runType && ACTIVITY_RUN_TYPES.includes(activity.runType)
      );
      
      const hasActivity = hasActivityInCurrent || hasActivityInExisting;
      
      console.log('[PointsService REST] hasHealthMetrics:', hasHealthMetrics, 'hasActivity:', hasActivity, 'earliestSubmissionTime:', earliestSubmissionTime);
      
      // Use earliest submission time for timing calculation
      const timingPoints = this.calculateTimingBasedPoints(
        earliestSubmissionTime,
        activityDate,
        hasHealthMetrics,
        hasActivity
      );
      
      console.log('[PointsService REST] Timing points:', timingPoints);
      
      // Get current user points via REST API
      console.log('[PointsService REST] Fetching points document...');
      const pointsDoc = await firestoreRestApi.getDocument('points', `${teamId}_${userId}`);
      console.log('[PointsService REST] Points document:', pointsDoc ? 'found' : 'not found');
      
      let currentStreak = 0;
      let currentExperiencePoints = 0;
      let lastLoggedDate = '';
      let lastHealthMetricsDate = '';
      let lastActivityDate = '';
      let existingData: any = {};
      
      if (pointsDoc) {
        currentStreak = pointsDoc.currentStreak || 0;
        currentExperiencePoints = pointsDoc.experiencePoints || 0;
        lastLoggedDate = pointsDoc.lastLoggedDate || '';
        lastHealthMetricsDate = pointsDoc.lastHealthMetricsDate || '';
        lastActivityDate = pointsDoc.lastActivityDate || '';
        existingData = pointsDoc;
      }
      
      console.log('[PointsService REST] Current state:', { currentStreak, currentExperiencePoints, lastHealthMetricsDate, lastActivityDate });
      
      // Calculate streak from activity history to avoid order-dependent regressions.
      const recomputedStreak = await this.recomputeStreakFromActivities(userId, teamId);
      let newStreak = 1;
      let newLastLoggedDate = activityDate;
      if (recomputedStreak) {
        newStreak = recomputedStreak.currentStreak;
        newLastLoggedDate = recomputedStreak.lastLoggedDate;
      } else {
        // Fallback to previous logic if history lookup fails.
        const activityDateObj = new Date(activityDate + 'T12:00:00');
        const yesterday = new Date(activityDateObj);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];

        if (lastLoggedDate === yesterdayStr) {
          newStreak = currentStreak + 1;
        } else if (lastLoggedDate === activityDate) {
          newStreak = currentStreak;
        } else if (lastLoggedDate !== '') {
          newStreak = 1;
        }
      }
      
      // Check if already awarded
      const healthMetricsAlreadyAwarded = lastHealthMetricsDate === activityDate;
      const activityAlreadyAwarded = lastActivityDate === activityDate;
      
      console.log('[PointsService REST] Already awarded:', { healthMetricsAlreadyAwarded, activityAlreadyAwarded });
      
      // Calculate XP to award
      let healthXPToAward = 0;
      let activityXPToAward = 0;
      
      if (hasHealthMetrics && !healthMetricsAlreadyAwarded) {
        healthXPToAward = timingPoints.healthXP;
      }
      
      if (hasActivity && !activityAlreadyAwarded) {
        activityXPToAward = timingPoints.activityXP;
      }
      
      const totalXPToAward = healthXPToAward + activityXPToAward;
      const totalWPToAward = totalXPToAward;
      
      console.log('[PointsService REST] XP to award:', { healthXPToAward, activityXPToAward, totalXPToAward });
      
      const newExperiencePoints = currentExperiencePoints + totalXPToAward;
      const newLevel = this.calculateLevelFromXP(newExperiencePoints);
      
      const newLastHealthMetricsDate = hasHealthMetrics ? activityDate : lastHealthMetricsDate;
      const newLastActivityDate = hasActivity ? activityDate : lastActivityDate;
      
      // Update points document via REST API
      const userPointsData = {
        id: `${teamId}_${userId}`,
        userId,
        teamId,
        experiencePoints: newExperiencePoints,
        level: newLevel,
        walletPoints: (existingData.walletPoints || 0) + totalWPToAward,
        dailyWalletPoints: (existingData.dailyWalletPoints || 0) + totalWPToAward,
        points: (existingData.points || 0) + totalWPToAward,
        availablePoints: (existingData.availablePoints || 0) + totalWPToAward,
        currentStreak: newStreak,
        longestStreak: Math.max(existingData.longestStreak || 0, newStreak),
        lastLoggedDate: newLastLoggedDate,
        lastHealthMetricsDate: newLastHealthMetricsDate,
        lastActivityDate: newLastActivityDate,
        lastUpdated: new Date(),
        // Ensure all required fields exist
        challengePoints: existingData.challengePoints || 0,
        missionPoints: existingData.missionPoints || 0,
        spentPoints: existingData.spentPoints || 0,
        streakPoints: existingData.streakPoints || 0,
        createdAt: existingData.createdAt || new Date()
      };
      
      console.log('[PointsService REST] Updating points document with:', { totalXPToAward, totalWPToAward, newLevel, newStreak });
      await firestoreRestApi.setDocument('points', `${teamId}_${userId}`, userPointsData);
      console.log('[PointsService REST] Points updated successfully');
      
      return {
        experiencePoints: totalXPToAward,
        walletPoints: totalWPToAward,
        streakBonus: 0,
        newStreak,
        newLevel,
        timing: timingPoints.timing,
        healthXP: healthXPToAward,
        activityXP: activityXPToAward
      };
    } catch (error: any) {
      console.error('[PointsService REST] ERROR in awardDailyLoggingPointsViaRest:', error);
      console.error('[PointsService REST] Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name,
        fullError: JSON.stringify(error, null, 2)
      });
      throw error; // Re-throw to be caught by caller
    }
  },

  // Award daily logging points (XP and WP)
  async awardDailyLoggingPoints(
    userId: string, 
    teamId: string, 
    activityDate: string,
    submissionDate: Date,
    activityData?: {
      sleepScore?: number;
      hydrationLevel?: number;
      nutritionLevel?: number;
      runType?: 'Run' | 'Workout' | 'Strength' | 'Cross Training' | 'Rest Day' | 'Sick' | 'Injured' | 'None';
      milesRun?: number;
      crossTrainingMinutes?: number;
      strengthMinutes?: number;
    }
  ): Promise<{
    experiencePoints: number;
    walletPoints: number;
    streakBonus: number;
    newStreak: number;
    newLevel: number;
    timing: string;
    healthXP: number;
    activityXP: number;
  }> {
    // Check if on iOS and use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      console.log('[PointsService] iOS detected - using REST API for awardDailyLoggingPoints');
      return await this.awardDailyLoggingPointsViaRest(userId, teamId, activityDate, submissionDate, activityData);
    }
    
    // Web/Android - use normal Firebase SDK with transactions
    try {
    // Fetch existing activities for this date to determine:
    // 1. If health metrics exist (across all activities for the date)
    // 2. If activity exists (across all activities for the date)
    // 3. The earliest submission time (for accurate timing calculation)
    let existingActivities: any[] = [];
    let earliestSubmissionTime: Date = submissionDate;
    
    try {
      const { activityService } = await import('./activityService');
      existingActivities = await activityService.getUserActivitiesForDate(userId, activityDate, teamId);
      
      // Find earliest submission time from all activities for this date
      if (existingActivities.length > 0) {
        const submissionTimes = existingActivities
          .map(activity => {
            if (activity.submittedAt) {
              return activity.submittedAt.toDate ? activity.submittedAt.toDate() : new Date(activity.submittedAt);
            }
            return null;
          })
          .filter((time): time is Date => time !== null);
        
        if (submissionTimes.length > 0) {
          earliestSubmissionTime = submissionTimes.reduce((earliest, current) => 
            current < earliest ? current : earliest
          );
        }
      }
    } catch (fetchError) {
      console.warn('Could not fetch existing activities for timing calculation:', fetchError);
      // Continue with current submission time if fetch fails
    }
    
    // Determine if health metrics exist (in current submission OR existing activities)
    const hasHealthMetricsInCurrent = activityData ? (
      (activityData.sleepScore !== undefined && activityData.sleepScore !== null && activityData.sleepScore > 0) ||
      (activityData.hydrationLevel !== undefined && activityData.hydrationLevel !== null && activityData.hydrationLevel > 0) ||
      (activityData.nutritionLevel !== undefined && activityData.nutritionLevel !== null && activityData.nutritionLevel > 0)
    ) : false;
    
    const hasHealthMetricsInExisting = existingActivities.some(activity =>
      (activity.sleepScore > 0) || (activity.hydrationLevel > 0) || (activity.nutritionLevel > 0)
    );
    
    const hasHealthMetrics = hasHealthMetricsInCurrent || hasHealthMetricsInExisting;
    
    // Determine if activity exists (in current submission OR existing activities)
    // All run activity types earn points: Run, Workout (legacy), Strength, Cross Training, Rest Day, Sick, Injured
    // No miles/minutes required - recording the activity type is enough
    const hasActivityInCurrent = activityData ? (
      activityData.runType !== undefined && 
      activityData.runType !== null && 
      activityData.runType !== 'None' &&
      ACTIVITY_RUN_TYPES.includes(activityData.runType)
    ) : false;
    
    const hasActivityInExisting = existingActivities.some(activity =>
      activity.runType && ACTIVITY_RUN_TYPES.includes(activity.runType)
    );
    
    const hasActivity = hasActivityInCurrent || hasActivityInExisting;
    
    // Use earliest submission time for timing calculation
    const timingPoints = this.calculateTimingBasedPoints(
      earliestSubmissionTime, 
      activityDate,
      hasHealthMetrics,
      hasActivity
    );
    
    // Get current user points
    const userPointsRef = doc(db, 'points', `${teamId}_${userId}`);
    const userPointsDoc = await getDoc(userPointsRef);
    
    let currentStreak = 0;
    let currentExperiencePoints = 0;
    let lastLoggedDate = '';
    let lastHealthMetricsDate = '';
    let lastActivityDate = '';
    
    if (userPointsDoc.exists()) {
      const userData = userPointsDoc.data() as Points;
      currentStreak = userData.currentStreak || 0;
      currentExperiencePoints = userData.experiencePoints || 0;
      lastLoggedDate = userData.lastLoggedDate || '';
      lastHealthMetricsDate = userData.lastHealthMetricsDate || '';
      lastActivityDate = userData.lastActivityDate || '';
    }
    
    // Calculate streak from activity history (not from write order).
    const recomputedStreak = await this.recomputeStreakFromActivities(userId, teamId);
    let newStreak = 1;
    let newLastLoggedDate = activityDate;
    if (recomputedStreak) {
      newStreak = recomputedStreak.currentStreak;
      newLastLoggedDate = recomputedStreak.lastLoggedDate;
    } else {
      // Fallback to legacy logic if history lookup fails.
      const activityDateObj = new Date(activityDate + 'T12:00:00'); // Use noon to avoid timezone issues
      const yesterday = new Date(activityDateObj);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];

      if (lastLoggedDate === yesterdayStr) {
        // Consecutive day - increment streak
        newStreak = currentStreak + 1;
      } else if (lastLoggedDate === activityDate) {
        // Same day - keep current streak
        newStreak = currentStreak;
      } else if (lastLoggedDate !== '') {
        // Streak broken - start new streak
        newStreak = 1;
      }
    }
    
    // Check if health metrics XP has already been awarded for this date
    const healthMetricsAlreadyAwarded = lastHealthMetricsDate === activityDate;
    
    // Check if activity XP has already been awarded for this date
    const activityAlreadyAwarded = lastActivityDate === activityDate;
    
    // Calculate XP to award:
    // - Award health XP if hasHealthMetrics is true AND it hasn't been awarded yet for this date
    // - Award activity XP if hasActivity is true AND it hasn't been awarded yet for this date
    let healthXPToAward = 0;
    let activityXPToAward = 0;
    
    if (hasHealthMetrics && !healthMetricsAlreadyAwarded) {
      healthXPToAward = timingPoints.healthXP;
    }
    
    if (hasActivity && !activityAlreadyAwarded) {
      activityXPToAward = timingPoints.activityXP;
    }
    
    const totalXPToAward = healthXPToAward + activityXPToAward;
    const totalWPToAward = totalXPToAward; // WP follows XP
    
    // Calculate new XP
    const newExperiencePoints = currentExperiencePoints + totalXPToAward;
    
    // Calculate new level from XP (Level = floor(XP) + 1, minimum level 1)
    const newLevel = this.calculateLevelFromXP(newExperiencePoints);
    
    // Update tracking dates
    const newLastHealthMetricsDate = hasHealthMetrics ? activityDate : lastHealthMetricsDate;
    const newLastActivityDate = hasActivity ? activityDate : lastActivityDate;
    
    await runTransaction(db, async (transaction) => {
      // Get existing data or initialize defaults
      const existingData = userPointsDoc.exists() ? (userPointsDoc.data() as Points) : {} as Points;
      
      // Update user points
      const userPointsData = {
        // Experience Points (XP) - Leveling system
        experiencePoints: newExperiencePoints,
        level: newLevel,
        
        // Wallet Points (WP) - Spendable currency
        walletPoints: (existingData.walletPoints || 0) + totalWPToAward,
        dailyWalletPoints: (existingData.dailyWalletPoints || 0) + totalWPToAward,
        
        // Legacy fields for backward compatibility
        points: (existingData.points || 0) + totalWPToAward,
        availablePoints: (existingData.availablePoints || 0) + totalWPToAward,
        
        // Metadata (keep streak tracking for display, but no bonus points)
        currentStreak: newStreak,
        longestStreak: Math.max(existingData.longestStreak || 0, newStreak),
        lastLoggedDate: newLastLoggedDate,
        lastHealthMetricsDate: newLastHealthMetricsDate,
        lastActivityDate: newLastActivityDate,
        lastUpdated: Timestamp.now()
      };

      if (userPointsDoc.exists()) {
        // For existing documents, we need to ensure all new fields are present
        const updateData = {
          ...existingData, // Keep all existing data
          ...userPointsData, // Override with new calculated values
          // Ensure new fields are present even if they weren't before
          experiencePoints: userPointsData.experiencePoints,
          walletPoints: userPointsData.walletPoints,
          dailyWalletPoints: userPointsData.dailyWalletPoints,
          level: userPointsData.level,
          currentStreak: userPointsData.currentStreak,
          longestStreak: userPointsData.longestStreak,
          lastLoggedDate: userPointsData.lastLoggedDate,
          lastHealthMetricsDate: userPointsData.lastHealthMetricsDate,
          lastActivityDate: userPointsData.lastActivityDate,
          lastUpdated: userPointsData.lastUpdated
        };
        transaction.set(userPointsRef, updateData);
      } else {
        transaction.set(userPointsRef, {
          id: `${teamId}_${userId}`,
          userId,
          teamId,
          challengePoints: 0,
          missionPoints: 0,
          spentPoints: 0,
          streakPoints: 0, // Keep field for backward compatibility, but always 0
          createdAt: Timestamp.now(),
          ...userPointsData
        });
      }
      
      // Create points transaction record (only if points were awarded)
      if (totalWPToAward > 0) {
        const dailyTransactionRef = doc(collection(db, 'pointsTransactions'));
        transaction.set(dailyTransactionRef, {
          userId,
          teamId,
          points: totalWPToAward,
          source: 'daily_logging',
          description: `Daily logging (${timingPoints.timing})${hasHealthMetrics && !healthMetricsAlreadyAwarded ? ' - Health Metrics' : ''}${hasActivity && !activityAlreadyAwarded ? ' - Activity' : ''}`,
          createdAt: Timestamp.now()
        });
      }
    });
    
    const result = {
      experiencePoints: totalXPToAward,
      walletPoints: totalWPToAward,
      streakBonus: 0, // No longer used, but kept for backward compatibility
      newStreak,
      newLevel,
      timing: timingPoints.timing,
      healthXP: healthXPToAward,
      activityXP: activityXPToAward
    };
    
    return result;
    } catch (error: any) {
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        console.warn('[PointsService] SDK permission denied; retrying awardDailyLoggingPoints via REST API');
        return await this.awardDailyLoggingPointsViaRest(userId, teamId, activityDate, submissionDate, activityData);
      }
      throw error;
    }
  },

  // Award challenge completion points
  async awardChallengePoints(userId: string, teamId: string, points: number, challengeId: string): Promise<void> {
    await runTransaction(db, async (transaction) => {
      // Update user points
      const userPointsRef = doc(db, 'points', `${teamId}_${userId}`);
      const userPointsDoc = await getDoc(userPointsRef);
      
      const pointsData = {
        walletPoints: (userPointsDoc.exists() ? (userPointsDoc.data() as Points).walletPoints : 0) + points,
        challengePoints: (userPointsDoc.exists() ? (userPointsDoc.data() as Points).challengePoints : 0) + points,
        points: (userPointsDoc.exists() ? (userPointsDoc.data() as Points).points : 0) + points,
        availablePoints: (userPointsDoc.exists() ? (userPointsDoc.data() as Points).availablePoints : 0) + points,
        lastUpdated: Timestamp.now()
      };
      
      if (userPointsDoc.exists()) {
        transaction.update(userPointsRef, pointsData);
      } else {
        transaction.set(userPointsRef, {
          id: `${teamId}_${userId}`,
          userId,
          teamId,
          experiencePoints: 0,
          level: 0,
          dailyWalletPoints: 0,
          streakPoints: 0,
          missionPoints: 0,
          currentStreak: 0,
          longestStreak: 0,
          lastLoggedDate: '',
          spentPoints: 0,
          createdAt: Timestamp.now(),
          ...pointsData
        });
      }
      
      // Create transaction record
      const transactionRef = doc(collection(db, 'pointsTransactions'));
      transaction.set(transactionRef, {
        userId,
        teamId,
        points,
        source: 'challenge_completion',
        sourceId: challengeId,
        description: `Challenge completion`,
        createdAt: Timestamp.now()
      });
    });
  },

  // Award mission completion points
  async awardMissionPoints(userId: string, teamId: string, points: number, missionId: string): Promise<void> {
    await runTransaction(db, async (transaction) => {
      // Update user points
      const userPointsRef = doc(db, 'points', `${teamId}_${userId}`);
      const userPointsDoc = await getDoc(userPointsRef);
      
      const pointsData = {
        walletPoints: (userPointsDoc.exists() ? (userPointsDoc.data() as Points).walletPoints : 0) + points,
        missionPoints: (userPointsDoc.exists() ? (userPointsDoc.data() as Points).missionPoints : 0) + points,
        points: (userPointsDoc.exists() ? (userPointsDoc.data() as Points).points : 0) + points,
        availablePoints: (userPointsDoc.exists() ? (userPointsDoc.data() as Points).availablePoints : 0) + points,
        lastUpdated: Timestamp.now()
      };
      
      if (userPointsDoc.exists()) {
        transaction.update(userPointsRef, pointsData);
      } else {
        transaction.set(userPointsRef, {
          id: `${teamId}_${userId}`,
          userId,
          teamId,
          experiencePoints: 0,
          level: 0,
          dailyWalletPoints: 0,
          streakPoints: 0,
          challengePoints: 0,
          currentStreak: 0,
          longestStreak: 0,
          lastLoggedDate: '',
          spentPoints: 0,
          createdAt: Timestamp.now(),
          ...pointsData
        });
      }
      
      // Create transaction record
      const transactionRef = doc(collection(db, 'pointsTransactions'));
      transaction.set(transactionRef, {
        userId,
        teamId,
        points,
        source: 'mission_completion',
        sourceId: missionId,
        description: `Mission completion`,
        createdAt: Timestamp.now()
      });
    });
  },

  // Award points for goal completion
  async awardGoalPoints(userId: string, teamId: string, goalTitle: string): Promise<void> {
    const points = this.POINTS.GOAL_COMPLETION;
    
    await runTransaction(db, async (transaction) => {
      // ALL READS FIRST
      const userPointsRef = doc(db, 'points', `${teamId}_${userId}`);
      const teamPointsRef = doc(db, 'teamPoints', teamId);
      
      const userPointsDoc = await transaction.get(userPointsRef);
      const teamPointsDoc = await transaction.get(teamPointsRef);
      
      // ALL WRITES AFTER READS
      // Update user points
      if (userPointsDoc.exists()) {
        transaction.update(userPointsRef, {
          points: increment(points),
          availablePoints: increment(points),
          goalPoints: increment(points),
          lastUpdated: Timestamp.now()
        });
      } else {
        transaction.set(userPointsRef, {
          userId: userId,
          teamId: teamId,
          points: points,
          availablePoints: points,
          goalPoints: points,
          bonusPoints: 0,
          spentPoints: 0,
          lastUpdated: Timestamp.now(),
          createdAt: Timestamp.now()
        });
      }

      // Update team points
      
      if (teamPointsDoc.exists()) {
        transaction.update(teamPointsRef, {
          totalPoints: increment(points),
          lastUpdated: Timestamp.now()
        });
      }

      // Create points transaction record
      const transactionRef = doc(collection(db, 'pointsTransactions'));
      transaction.set(transactionRef, {
        userId: userId,
        teamId: teamId,
        points: points,
        source: 'goal_achievement',
        description: `Completed goal: ${goalTitle}`,
        createdAt: Timestamp.now()
      });
    });
  },

  // Check for achievements and award bonus points
  async checkAchievements(userId: string, teamId: string): Promise<void> {
    
  },




  // Award bonus points
  async awardBonusPoints(userId: string, teamId: string, points: number, description: string): Promise<void> {
    await runTransaction(db, async (transaction) => {
      // ALL READS FIRST
      const userPointsRef = doc(db, 'points', `${teamId}_${userId}`);
      const teamPointsRef = doc(db, 'teamPoints', teamId);
      
      const userPointsDoc = await transaction.get(userPointsRef);
      const teamPointsDoc = await transaction.get(teamPointsRef);
      
      // ALL WRITES AFTER READS
      // Update user points
      if (userPointsDoc.exists()) {
        transaction.update(userPointsRef, {
          points: increment(points),
          availablePoints: increment(points),
          bonusPoints: increment(points),
          lastUpdated: Timestamp.now()
        });
      } else {
        transaction.set(userPointsRef, {
          userId: userId,
          teamId: teamId,
          points: points,
          availablePoints: points,
          goalPoints: 0,
          bonusPoints: points,
          spentPoints: 0,
          lastUpdated: Timestamp.now(),
          createdAt: Timestamp.now()
        });
      }

      // Update team points
      if (teamPointsDoc.exists()) {
        transaction.update(teamPointsRef, {
          totalPoints: increment(points),
          lastUpdated: Timestamp.now()
        });
      }

      // Create points transaction record
      const transactionRef = doc(collection(db, 'pointsTransactions'));
      transaction.set(transactionRef, {
        userId: userId,
        teamId: teamId,
        points: points,
        source: 'bonus',
        description: description,
        createdAt: Timestamp.now()
      });
    });
  },

  // Create achievement record
  async createAchievement(userId: string, teamId: string, type: string, title: string, description: string, pointsAwarded: number, icon?: string): Promise<void> {
    await addDoc(collection(db, 'achievements'), {
      userId: userId,
      teamId: teamId,
      type: type,
      title: title,
      description: description,
      pointsAwarded: pointsAwarded,
      icon: icon,
      unlockedAt: Timestamp.now(),
      createdAt: Timestamp.now()
    });
  },

  // Get achievement by type
  async getAchievementByType(userId: string, teamId: string, type: string, value: number): Promise<Achievement | null> {
    const q = query(
      collection(db, 'achievements'),
      where('userId', '==', userId),
      where('teamId', '==', teamId),
      where('type', '==', type)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.docs.length === 0) return null;
    
    return {
      id: querySnapshot.docs[0].id,
      ...querySnapshot.docs[0].data(),
      unlockedAt: querySnapshot.docs[0].data().unlockedAt,
      createdAt: querySnapshot.docs[0].data().createdAt,
    } as Achievement;
  },

  // Get user points
  async getUserPoints(userId: string, teamId: string): Promise<Points | null> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const pointsDoc = await firestoreRestApi.getDocument('points', `${teamId}_${userId}`);
        if (!pointsDoc) return null;
        
        const data = pointsDoc.data();
        const calculatedAvailablePoints = (data.points || 0) - (data.spentPoints || 0);
        
        // Recalculate level from XP to ensure it's always correct
        const experiencePoints = data.experiencePoints || 0;
        const recalculatedLevel = this.calculateLevelFromXP(experiencePoints);
        
        return {
          id: pointsDoc.id,
          ...data,
          level: recalculatedLevel,
          walletPoints: data.walletPoints ?? calculatedAvailablePoints,
          availablePoints: calculatedAvailablePoints,
          lastUpdated: data.lastUpdated || Timestamp.now(),
          createdAt: data.createdAt || Timestamp.now(),
        } as Points;
      } catch (error) {
        console.error('Error fetching user points via REST API:', error);
        return null;
      }
    }
    
    // On web/Android, use SDK with REST fallback
    try {
      const pointsDocRef = doc(db, 'points', `${teamId}_${userId}`);
      const pointsDoc = await getDoc(pointsDocRef);
      
      if (!pointsDoc.exists()) {
        return null;
      }
      
      const data = pointsDoc.data();
      const calculatedAvailablePoints = (data.points || 0) - (data.spentPoints || 0);
      
      const experiencePoints = data.experiencePoints || 0;
      const recalculatedLevel = this.calculateLevelFromXP(experiencePoints);
      
      return {
        id: pointsDoc.id,
        ...data,
        level: recalculatedLevel,
        walletPoints: data.walletPoints ?? calculatedAvailablePoints,
        availablePoints: calculatedAvailablePoints,
        lastUpdated: data.lastUpdated,
        createdAt: data.createdAt,
      } as Points;
    } catch (error: any) {
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        try {
          const pointsDoc = await firestoreRestApi.getDocument('points', `${teamId}_${userId}`);
          if (!pointsDoc) return null;
          const data = pointsDoc.data();
          const calculatedAvailablePoints = (data.points || 0) - (data.spentPoints || 0);
          const experiencePoints = data.experiencePoints || 0;
          const recalculatedLevel = this.calculateLevelFromXP(experiencePoints);
          return {
            id: pointsDoc.id,
            ...data,
            level: recalculatedLevel,
            walletPoints: data.walletPoints ?? calculatedAvailablePoints,
            availablePoints: calculatedAvailablePoints,
            lastUpdated: data.lastUpdated || Timestamp.now(),
            createdAt: data.createdAt || Timestamp.now(),
          } as Points;
        } catch (restError) {
          console.error('Error fetching user points via REST fallback:', restError);
          return null;
        }
      }
      throw error;
    }
  },

  // Get team points
  async getTeamPoints(teamId: string): Promise<TeamPoints | null> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const teamPointsDoc = await firestoreRestApi.getDocument('teamPoints', teamId);
        if (!teamPointsDoc) return null;
        
        const data = teamPointsDoc.data();
        return {
          id: teamPointsDoc.id,
          ...data,
          lastUpdated: data.lastUpdated || Timestamp.now(),
          createdAt: data.createdAt || Timestamp.now(),
        } as TeamPoints;
      } catch (error) {
        console.error('Error fetching team points via REST API:', error);
        return null;
      }
    }
    
    // On web/Android, use SDK with REST fallback
    try {
      const teamPointsDoc = await getDoc(doc(db, 'teamPoints', teamId));
      
      if (!teamPointsDoc.exists()) return null;
      
      return {
        id: teamPointsDoc.id,
        ...teamPointsDoc.data(),
        lastUpdated: teamPointsDoc.data().lastUpdated,
        createdAt: teamPointsDoc.data().createdAt,
      } as TeamPoints;
    } catch (error: any) {
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        console.warn('[PointsService] SDK permission denied for getTeamPoints; retrying via REST API');
        try {
          const teamPointsDoc = await firestoreRestApi.getDocument('teamPoints', teamId);
          if (!teamPointsDoc) return null;
          const data = teamPointsDoc.data();
          return {
            id: teamPointsDoc.id,
            ...data,
            lastUpdated: data.lastUpdated || Timestamp.now(),
            createdAt: data.createdAt || Timestamp.now(),
          } as TeamPoints;
        } catch (restError) {
          console.error('Error fetching team points via REST fallback:', restError);
          return null;
        }
      }
      throw error;
    }
  },

  // Get team leaderboard
  async getTeamLeaderboard(teamId: string): Promise<Points[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'points',
          [{ field: 'teamId', op: '==', value: teamId }]
        );
        
        // Sort by experiencePoints in JavaScript
        const sortedDocs = documents.sort((a, b) => {
          const aXP = a.data().experiencePoints || 0;
          const bXP = b.data().experiencePoints || 0;
          return bXP - aXP; // Descending order
        });
        
        return sortedDocs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            lastUpdated: data.lastUpdated ? Timestamp.fromDate(new Date(data.lastUpdated)) : Timestamp.now(),
            createdAt: data.createdAt ? Timestamp.fromDate(new Date(data.createdAt)) : Timestamp.now(),
            totalPoints: data.experiencePoints || 0
          } as Points;
        });
      } catch (error) {
        console.error('Error fetching team leaderboard via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK with REST fallback
    try {
      const q = query(
        collection(db, 'points'),
        where('teamId', '==', teamId)
      );
      const querySnapshot = await getDocs(q);
      
      // Sort by experiencePoints in JavaScript (to avoid Firestore index requirement)
      const sortedDocs = querySnapshot.docs.sort((a, b) => {
        const aXP = a.data().experiencePoints || 0;
        const bXP = b.data().experiencePoints || 0;
        return bXP - aXP; // Descending order
      });
      
      return sortedDocs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          lastUpdated: data.lastUpdated,
          createdAt: data.createdAt,
          // Leaderboard shows XP points only
          totalPoints: data.experiencePoints || 0
        } as Points;
      });
    } catch (error: any) {
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        console.warn('[PointsService] SDK permission denied for getTeamLeaderboard; retrying via REST API');
        try {
          const documents = await firestoreRestApi.queryDocuments(
            'points',
            [{ field: 'teamId', op: '==', value: teamId }]
          );
          
          // Sort by experiencePoints in JavaScript
          const sortedDocs = documents.sort((a, b) => {
            const aXP = a.data().experiencePoints || 0;
            const bXP = b.data().experiencePoints || 0;
            return bXP - aXP; // Descending order
          });
          
          return sortedDocs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              lastUpdated: data.lastUpdated ? Timestamp.fromDate(new Date(data.lastUpdated)) : Timestamp.now(),
              createdAt: data.createdAt ? Timestamp.fromDate(new Date(data.createdAt)) : Timestamp.now(),
              totalPoints: data.experiencePoints || 0
            } as Points;
          });
        } catch (restError) {
          console.error('Error fetching team leaderboard via REST fallback:', restError);
          return [];
        }
      }
      throw error;
    }
  },

  // Get user achievements
  async getUserAchievements(userId: string, teamId: string): Promise<Achievement[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'achievements',
          [
            { field: 'userId', op: '==', value: userId },
            { field: 'teamId', op: '==', value: teamId }
          ]
        );
        
        // Sort by unlockedAt descending (client-side since REST API doesn't support orderBy easily)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().unlockedAt ? new Date(a.data().unlockedAt).getTime() : 0;
          const bTime = b.data().unlockedAt ? new Date(b.data().unlockedAt).getTime() : 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data(),
          unlockedAt: doc.data().unlockedAt ? Timestamp.fromDate(new Date(doc.data().unlockedAt)) : Timestamp.now(),
          createdAt: doc.data().createdAt ? Timestamp.fromDate(new Date(doc.data().createdAt)) : Timestamp.now(),
        })) as Achievement[];
      } catch (error) {
        console.error('Error fetching user achievements via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    const q = query(
      collection(db, 'achievements'),
      where('userId', '==', userId),
      where('teamId', '==', teamId),
      orderBy('unlockedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      unlockedAt: doc.data().unlockedAt,
      createdAt: doc.data().createdAt,
    })) as Achievement[];
  },

  // Get points transactions for user
  async getUserPointsTransactions(userId: string, teamId: string, limit: number = 20): Promise<PointsTransaction[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'pointsTransactions',
          [
            { field: 'userId', op: '==', value: userId },
            { field: 'teamId', op: '==', value: teamId }
          ],
          limit
        );
        
        // Sort by createdAt descending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().createdAt ? new Date(a.data().createdAt).getTime() : 0;
          const bTime = b.data().createdAt ? new Date(b.data().createdAt).getTime() : 0;
          return bTime - aTime;
        });
        
        return sorted.slice(0, limit).map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt ? Timestamp.fromDate(new Date(doc.data().createdAt)) : Timestamp.now(),
        })) as PointsTransaction[];
      } catch (error) {
        console.error('Error fetching points transactions via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    const q = query(
      collection(db, 'pointsTransactions'),
      where('userId', '==', userId),
      where('teamId', '==', teamId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.slice(0, limit).map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt,
    })) as PointsTransaction[];
  },

  // Update team points averages
  async updateTeamPointsAverages(teamId: string): Promise<void> {
    const teamPoints = await this.getTeamPoints(teamId);
    if (!teamPoints) return;
    
    const leaderboard = await this.getTeamLeaderboard(teamId);
    const averagePoints = leaderboard.length > 0 ? teamPoints.totalPoints / leaderboard.length : 0;
    
    await updateDoc(doc(db, 'teamPoints', teamId), {
      memberCount: leaderboard.length,
      averagePoints: averagePoints,
      lastUpdated: Timestamp.now()
    });
  },

  // Spend points on a prize
  async spendPoints(userId: string, teamId: string, pointsToSpend: number, description: string): Promise<void> {
    // On iOS, use REST API first (SDK runTransaction can hang due to auth)
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const pointsDoc = await firestoreRestApi.getDocument('points', `${teamId}_${userId}`);
        if (!pointsDoc) throw new Error('User points not found');
        const data = pointsDoc.data();
        const currentWallet = Number(data.walletPoints ?? data.availablePoints ?? ((data.points || 0) - (data.spentPoints || 0)));
        const toSpend = Number(pointsToSpend);
        if (!Number.isFinite(currentWallet) || currentWallet < toSpend || currentWallet <= 0) {
          throw new Error('Insufficient available points');
        }
        await firestoreRestApi.setDocument('points', `${teamId}_${userId}`, {
          walletPoints: currentWallet - toSpend,
          availablePoints: currentWallet - toSpend,
          spentPoints: Number(data.spentPoints || 0) + toSpend,
          lastUpdated: new Date()
        }, true);
        await firestoreRestApi.createDocument('pointsTransactions', {
          userId,
          teamId,
          points: -pointsToSpend,
          source: 'prize_redemption',
          description,
          createdAt: new Date()
        });
        return;
      } catch (err: any) {
        console.error('[PointsService] iOS REST spendPoints failed:', err);
        throw err;
      }
    }

    try {
      await runTransaction(db, async (transaction) => {
        const userPointsRef = doc(db, 'points', `${teamId}_${userId}`);
        const userPointsDoc = await transaction.get(userPointsRef);
        
        if (!userPointsDoc.exists()) {
          throw new Error('User points not found');
        }
        
        const currentPoints = userPointsDoc.data();
        const walletBalance = currentPoints.walletPoints ?? currentPoints.availablePoints ?? 0;
        if (walletBalance < pointsToSpend) {
          throw new Error('Insufficient available points');
        }
        
        // Deduct from wallet points (and availablePoints); track spent for display
        transaction.update(userPointsRef, {
          walletPoints: increment(-pointsToSpend),
          availablePoints: increment(-pointsToSpend),
          spentPoints: increment(pointsToSpend),
          lastUpdated: Timestamp.now()
        });
        
        // Create points transaction record
        const transactionRef = doc(collection(db, 'pointsTransactions'));
        transaction.set(transactionRef, {
          userId: userId,
          teamId: teamId,
          points: -pointsToSpend,
          source: 'prize_redemption',
          description: description,
          createdAt: Timestamp.now()
        });
      });
    } catch (error: any) {
      const isPermissionError = error?.code === 'permission-denied' || error?.message?.includes('permission') || error?.message?.includes('insufficient permissions');
      if (isPermissionError) {
        try {
          console.warn('[PointsService] SDK permission denied for spendPoints; retrying via REST API');
          const pointsDoc = await firestoreRestApi.getDocument('points', `${teamId}_${userId}`);
          if (!pointsDoc) throw new Error('User points not found');
          const data = pointsDoc.data();
          const currentWallet = Number(data.walletPoints ?? data.availablePoints ?? ((data.points || 0) - (data.spentPoints || 0)));
          const toSpend = Number(pointsToSpend);
          if (!Number.isFinite(currentWallet) || currentWallet < toSpend || currentWallet <= 0) {
            throw new Error('Insufficient available points');
          }
          await firestoreRestApi.setDocument('points', `${teamId}_${userId}`, {
            walletPoints: currentWallet - toSpend,
            availablePoints: currentWallet - toSpend,
            spentPoints: Number(data.spentPoints || 0) + toSpend,
            lastUpdated: new Date()
          }, true);
          await firestoreRestApi.createDocument('pointsTransactions', {
            userId,
            teamId,
            points: -pointsToSpend,
            source: 'prize_redemption',
            description,
            createdAt: new Date()
          });
          return;
        } catch (restErr: any) {
          console.error('[PointsService] REST fallback for spendPoints failed:', restErr);
          throw restErr;
        }
      }
      throw error;
    }
  },

  // Refund points (if prize redemption is denied)
  async refundPoints(userId: string, teamId: string, pointsToRefund: number, description: string): Promise<void> {
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const pointsDoc = await firestoreRestApi.getDocument('points', `${teamId}_${userId}`);
        if (!pointsDoc) throw new Error('User points not found');
        const data = pointsDoc.data();
        const currentAvailable = Number(data.availablePoints ?? 0);
        const currentSpent = Number(data.spentPoints ?? 0);
        const currentWallet = Number(data.walletPoints ?? data.availablePoints ?? 0);
        await firestoreRestApi.setDocument('points', `${teamId}_${userId}`, {
          availablePoints: currentAvailable + pointsToRefund,
          spentPoints: Math.max(0, currentSpent - pointsToRefund),
          walletPoints: currentWallet + pointsToRefund,
          lastUpdated: new Date()
        }, true);
        await firestoreRestApi.createDocument('pointsTransactions', {
          userId,
          teamId,
          points: pointsToRefund,
          source: 'prize_refund',
          description,
          createdAt: new Date()
        });
        return;
      } catch (err: any) {
        console.error('[PointsService] iOS REST refundPoints failed:', err);
        throw err;
      }
    }
    try {
      await runTransaction(db, async (transaction) => {
        const userPointsRef = doc(db, 'points', `${teamId}_${userId}`);
        const userPointsDoc = await transaction.get(userPointsRef);

        if (!userPointsDoc.exists()) {
          throw new Error('User points not found');
        }

        transaction.update(userPointsRef, {
          availablePoints: increment(pointsToRefund),
          spentPoints: increment(-pointsToRefund),
          lastUpdated: Timestamp.now()
        });

        const transactionRef = doc(collection(db, 'pointsTransactions'));
        transaction.set(transactionRef, {
          userId: userId,
          teamId: teamId,
          points: pointsToRefund,
          source: 'prize_refund',
          description: description,
          createdAt: Timestamp.now()
        });
      });
    } catch (error: any) {
      const isPermissionError = error?.code === 'permission-denied' || error?.message?.includes('permission') || error?.message?.includes('insufficient permissions');
      if (isPermissionError) {
        try {
          console.warn('[PointsService] SDK permission denied for refundPoints; retrying via REST API');
          const pointsDoc = await firestoreRestApi.getDocument('points', `${teamId}_${userId}`);
          if (!pointsDoc) throw new Error('User points not found');
          const data = pointsDoc.data();
          const currentAvailable = Number(data.availablePoints ?? 0);
          const currentSpent = Number(data.spentPoints ?? 0);
          const currentWallet = Number(data.walletPoints ?? data.availablePoints ?? 0);
          await firestoreRestApi.setDocument('points', `${teamId}_${userId}`, {
            availablePoints: currentAvailable + pointsToRefund,
            spentPoints: Math.max(0, currentSpent - pointsToRefund),
            walletPoints: currentWallet + pointsToRefund,
            lastUpdated: new Date()
          }, true);
          await firestoreRestApi.createDocument('pointsTransactions', {
            userId,
            teamId,
            points: pointsToRefund,
            source: 'prize_refund',
            description,
            createdAt: new Date()
          });
          return;
        } catch (restErr: any) {
          console.error('[PointsService] REST fallback for refundPoints failed:', restErr);
          throw restErr;
        }
      }
      throw error;
    }
  },


  // Get points summary for user
  async getPointsSummary(userId: string, teamId: string): Promise<{
    // Experience Points (XP)
    level: number;
    levelDisplayString: string; // Display string (handles prestige level)
    isPrestigeLevel: boolean; // Whether user is at prestige level
    experiencePoints: number;
    
    // Wallet Points (WP)
    walletPoints: number;
    dailyWalletPoints: number;
    streakPoints: number;
    challengePoints: number;
    missionPoints: number;
    
    // Streak info
    currentStreak: number;
    longestStreak: number;
    
    // Legacy fields for backward compatibility
    totalPoints: number;
    availablePoints: number;
    spentPoints: number;
    goalPoints: number;
    bonusPoints: number;
    rank: number;
    achievements: Achievement[];
    recentTransactions: PointsTransaction[];
  }> {
    const [pointsRes, achievementsRes, transactionsRes, leaderboardRes] = await Promise.allSettled([
      this.getUserPoints(userId, teamId),
      this.getUserAchievements(userId, teamId),
      this.getUserPointsTransactions(userId, teamId, 10),
      this.getTeamLeaderboard(teamId)
    ]);

    const points = pointsRes.status === 'fulfilled' ? pointsRes.value : null;
    const achievements = achievementsRes.status === 'fulfilled' ? achievementsRes.value : [];
    const transactions = transactionsRes.status === 'fulfilled' ? transactionsRes.value : [];
    const leaderboard = leaderboardRes.status === 'fulfilled' ? leaderboardRes.value : [];
    
    console.log('[PointsService] getPointsSummary - getUserPoints result:', points);
    console.log('[PointsService] getPointsSummary - experiencePoints from points:', points?.experiencePoints);
    
    const userPoints = points || {
      // Experience Points (XP)
      level: 0,
      experiencePoints: 0,
      
      // Wallet Points (WP)
      walletPoints: 0,
      dailyWalletPoints: 0,
      streakPoints: 0,
      challengePoints: 0,
      missionPoints: 0,
      
      // Streak info
      currentStreak: 0,
      longestStreak: 0,
      
      // Legacy fields
      points: 0,
      availablePoints: 0,
      spentPoints: 0,
      goalPoints: 0,
      bonusPoints: 0
    };
    
    const rank = leaderboard.findIndex(p => p.userId === userId) + 1;
    
    const experiencePoints = userPoints.experiencePoints || 0;
    const level = this.calculateLevelFromXP(experiencePoints);
    const isPrestige = this.isPrestigeLevel(experiencePoints);
    const levelDisplayString = this.getLevelDisplayString(experiencePoints);
    
    // Wallet points = spendable balance (purchasing power for prizes)
    const walletPoints = Number(userPoints.walletPoints ?? userPoints.availablePoints ?? (userPoints.points ?? 0) - (userPoints.spentPoints ?? 0));
    const spent = Number(userPoints.spentPoints ?? 0);
    // Total points earned (for display) = current wallet + what they've spent
    const totalEarned = Math.max(0, walletPoints) + spent;
    
    const result = {
      // Experience Points (XP)
      level,
      levelDisplayString,
      isPrestigeLevel: isPrestige,
      experiencePoints,
      
      // Wallet Points (WP)
      walletPoints: userPoints.walletPoints || 0,
      dailyWalletPoints: userPoints.dailyWalletPoints || 0,
      streakPoints: userPoints.streakPoints || 0,
      challengePoints: userPoints.challengePoints || 0,
      missionPoints: userPoints.missionPoints || 0,
      
      // Streak info
      currentStreak: userPoints.currentStreak || 0,
      longestStreak: userPoints.longestStreak || 0,
      
      // Legacy fields for backward compatibility (Prizes page: Total Earned, Available, Spent)
      totalPoints: totalEarned,
      availablePoints: Math.max(0, walletPoints),
      spentPoints: spent,
      goalPoints: 0, // Legacy field - not used in new system
      bonusPoints: 0, // Legacy field - not used in new system
      rank: rank || leaderboard.length + 1,
      achievements,
      recentTransactions: transactions
    };
    
    return result;
  }
};
